import React, {useEffect, useRef, useState} from "react";
import {Button, Popover, Select, Switch, Table, Tabs, Tooltip, Typography} from "antd";
import {v4} from "uuid";
import HecRasHelper from "../helpers/HecRasHelper";
import ProjectInfo from "./ProjectInfo";
import {PlanProfiles, PlanTypes} from "./ProjectUploader";
import {useHistory} from "react-router-dom";
import {CaretRightOutlined, CloseOutlined, DownloadOutlined, MinusOutlined, PlusOutlined, WarningOutlined} from "@ant-design/icons";
import {ModelNames} from "../context/AppContext";
import ShapeTable from "./map/ShapeTable";
import {useModal} from "../context/ModalContext";
import HecRasOutputTable from "./HecRasOutputTable";
import LocalService from "../services/LocalService";

export default function HecRasProject({project, onProjectChanged, onSaveProject}) {
    const [loading, setLoading] = useState(false)
    const expandProjects = useRef({})
    const history = useHistory()
    const [tempProject, setTempProject] = useState({
        isChanged: false,
        useProjectLabel: true,
        useProfileLabel: true,
        notes: '',
        type: 'def',
        information: {
            folderName: '',
            title: '',
            descriptions: [],
            text: '',
            programVersion: '',
            outputColumns: [],
        },
        projectFiles: [],
        files: [],
    })
    const [expandedKeys, setExpandedKeys] = useState([])
    const [tab, setTab] = useState('Project Files')
    const [isRunOutput, setRunOutput] = useState(false)
    const [runOutputText, setRunOutputText] = useState('')
    const {openModal, closeModal} = useModal()

    useEffect(() => {
        if (project) {
            const keys = []
            project.projectFiles?.forEach(x => {
                if (x.mapProfiles.filter(x => x.target).length > 0)
                    keys.push(x.id)
            })

            if(!expandProjects[project.id]) {
                setExpandedKeys(keys)
                setTempProject({
                    ...project,
                    isChanged: false,
                    key: v4()
                })
            }
            else {
                setExpandedKeys(expandProjects[project.id])
            }
        }
    }, [project])

    useEffect(() => {
        if (tempProject?.isChanged) {
            onProjectChanged({
                ...tempProject,
                version: getVersions()
            })
        }
    }, [tempProject, tempProject?.key, tempProject?.notes, tempProject?.isChanged])

    const renderFile = (file) => {
        if (file) {
            return (
                <div style={{marginLeft: 6}}>
                    <div><b>{file.title}</b></div>
                    <div
                        style={{wordWrap: 'break-word'}}
                    >
                        {file.fileName}
                    </div>
                </div>
            )
        }

        return (
            <b style={{color: '#ff0000'}}>Not Found</b>
        )
    }

    const renderOutputCol = (row) => {
        if (!row.profileData || Object.keys(row.profileData).length === 0)
            return (
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <b key={v4()} style={{color: '#ff0000'}}>
                        There is no output file
                    </b>
                </div>
            )

        if(row?.isFindOutput === 'false') {
            return (
                <div>
                    <b key={v4()} style={{color: '#ff0000'}}>Plan data is not available in the output file</b>
                </div>
            )
        }

        return (
            <Typography.Link
                onClick={() => {
                    openModal({
                        title: row.plan.shortIdentifier,
                        component: (
                            <HecRasOutputTable profileData={row.profileData} mapProfiles={row.mapProfiles} rivers={row.rivers || []}/>
                        ),
                        style: {width: HecRasHelper.getWidth(197)},
                        closable: true
                    })
                }}
            >
                {row.plan.shortIdentifier}
            </Typography.Link>
        )
    }

    const onTypeChanged = (row, val) => {
        const index = tempProject.projectFiles.findIndex(x => x.id === row.id);
        if (index >= 0) {
            tempProject.projectFiles[index].type = val
            setTempProject({
                ...tempProject,
                isChanged: true
            })
        }
    }

    const remove = (row) => {
        const files = tempProject.projectFiles.filter(x => x.id !== row.id);
        let prjText = tempProject?.information?.text;

        let flowLine = `Flow File=${row?.flow?.ext.toLowerCase()}`
        let geomLine = `Geom File=${row?.geometry?.ext.toLowerCase()}`

        const isUseFlow = files.find(x => x.flow.ext.toLowerCase() === row.flow.ext.toLowerCase())
        const isUseGeom = files.find(x => x.geometry.ext.toLowerCase() === row.geometry.ext.toLowerCase())

        tempProject.information.text = tempProject.information.text.replaceAll(`Plan File=${row?.plan?.ext?.toLowerCase()}`, '')

        if(!isUseFlow)
            tempProject.information.text = tempProject.information.text.replaceAll(flowLine, '')

        if(!isUseGeom)
            tempProject.information.text = tempProject.information.text.replaceAll(geomLine, '')

        tempProject.information.text = tempProject.information.text.replace(/^\s*[\r\n]/gm, '')

        if(files.length > 0) {
            tempProject.information.text = tempProject.information.text.replace(/Current Plan=p\d+/g, `Current Plan=${files[0].plan.ext}`)
        }

        setTempProject({
            ...tempProject,
            projectFiles: files,
            isChanged: true
        })
    }

    const renderRowExpand = (row) => {
        const onChangeProfile = (select, val) => {
            setTempProject((prev) => {
                const data = {...prev}

                data.projectFiles = data.projectFiles.map(plan => {
                    if (plan.id === row.id) {
                        // plan.id = v4()
                        plan.mapProfiles = plan.mapProfiles.map(map => {
                            if (map.select === select) {
                                map.target = val
                                map.targetName = PlanProfiles.find(x => x.value === val)?.label || val
                            }

                            return map
                        })
                    }

                    return plan
                })

                data.projectFiles = data.projectFiles.map(plan => {
                    plan.mappedProfiles = HecRasHelper.mapProfiles(plan.profileData, plan.mapProfiles)
                    return plan
                })
                data.isChanged = true

                return data
            })
        }

        return (
            <div style={{marginLeft: 310}}>
                {
                    row?.needRiverColumn && (
                        <div style={{
                            width: '100%',
                            backgroundColor: 'red',
                            color: 'white',
                            height: 32,
                            verticalAlign: 'middle',
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: 8,
                            fontSize: '10pt',
                            fontWeight: 'bold',
                            marginBottom: 8
                        }}>
                            This plan has multiple rivers but river name column is missing in output file!
                        </div>
                    )
                }
                <div style={{maxHeight: 200, overflowY: 'auto', width: 450}}>
                    <table>
                        <tbody>
                        {row.mapProfiles.map((x, idx) => (
                            <tr key={idx.toString()}>
                                <td style={{width: 150}}>{x.select}</td>
                                <td>
                                    <Select
                                        size={'small'}
                                        defaultValue={x.target}
                                        placeholder={'Select Profile'}
                                        allowClear={true}
                                        onClear={() => {
                                            onChangeProfile(x.select, '')
                                        }}
                                        onChange={(val) => onChangeProfile(x.select, val)}
                                        style={{
                                            width: 200,
                                            marginBottom: 4,
                                            border: PlanProfiles.find(y => y.value === x.target) ? '1px solid #035781' : ''
                                        }}
                                    >
                                        <Select.Option value={x.select}>
                                            {x.select}
                                        </Select.Option>
                                        {
                                            PlanProfiles.map((y, idy) => (
                                                <Select.Option value={y.value} key={idy}>
                                                    {y.visible_label}
                                                </Select.Option>
                                            ))
                                        }
                                    </Select>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )
    }

    const getVersions = () => {
        const list = tempProject.projectFiles.map(x => x.plan.version).filter(x => x && x.trim());
        return [...new Set(list)].join(', ')
    }

    const getPlanId = (row) => {
        const splitted = row.plan.fileName.split('.')
        return `${row.plan.shortIdentifier}.${splitted[splitted.length - 1]}`
    }

    useEffect(() => {
        expandProjects[project.id] = expandedKeys
    }, [expandedKeys]);

    return (
        <>
            <div style={{marginBottom: 16}} key={v4()}>
                <div style={{display: 'flex', gap: 24}}>
                    <div style={{flex: 1}}>
                        <ProjectInfo
                            autoHeight={true}
                            autoHeightKey={'hecrasProject'}
                            folderName={tempProject?.information?.folderName}
                            title={tempProject?.information?.title}
                            descriptions={tempProject?.information?.descriptions}
                            showNotes={true}
                            notes={tempProject?.notes}
                            onChangeNotes={(e) => {
                                setTempProject({
                                    ...tempProject,
                                    notes: e.target.value,
                                    isChanged: true,
                                    key: v4()
                                })
                            }}
                            version={
                                tempProject?.projectFiles && tempProject?.projectFiles?.length > 0 && getVersions()
                            }
                        />
                    </div>
                    {
                        tempProject.information?.outputColumns?.length > 0 && (
                            <div
                                style={{
                                    backgroundColor: '#FBE5D6',
                                    padding: 4,
                                    border: '1px solid black',
                                    flex: 1,
                                }}
                            >
                                The following variables are missing in the output file. These variables are essential to
                                make the relevant checks and comparisons.
                                <div style={{color: '#ff0000'}}>
                                    {
                                        tempProject.information?.outputColumns?.join(', ')
                                    }
                                </div>
                            </div>
                        )
                    }
                    {
                        (tempProject.information?.hasOutputFile === 'false') && (
                            <div
                                style={{
                                    backgroundColor: '#FBE5D6',
                                    padding: 4,
                                    border: '1px solid black',
                                    flex: 1
                                }}
                            >
                                The output text file is missing in the project folder! RAS Review will still check and
                                compare geometry and flow that does not require the calculated water surface elevation. For
                                full functionality create and include the output text file as directed in
                                <a onClick={() => {
                                    history.push('/app/help?type=output')
                                }}>{' '}How to menu.</a>
                            </div>
                        )
                    }
                </div>
                <br/>
                <div key={tempProject.key}>
                    <Tabs
                        tabBarExtraContent={{
                            left: (
                                (
                                    <>
                                        <Select
                                            size={'small'}
                                            style={{width: 200, marginRight: 8}}
                                            value={tempProject?.modelType}
                                            suffixIcon={'Model'}
                                            onChange={(val) => {
                                                setTempProject({
                                                    ...tempProject,
                                                    modelType: val,
                                                    isChanged: true
                                                })
                                            }}
                                        >
                                            {
                                                ModelNames.map(name => (
                                                    <Select.Option key={`Model ${name}`} value={`Model ${name}`}>
                                                        {name}
                                                    </Select.Option>
                                                ))
                                            }
                                        </Select>
                                        <Button
                                            style={{width: 125, marginRight: 8}}
                                            type={'primary'}
                                            size={'small'}
                                            loading={loading}
                                            onClick={async (e) => {
                                                try {
                                                    setLoading(true)
                                                    const res = await onSaveProject({
                                                        ...tempProject,
                                                        version: getVersions()
                                                    })
                                                }
                                                catch (exp){
                                                    console.error(exp)
                                                }
                                                setLoading(false)
                                            }}
                                        >
                                            Save Project
                                        </Button>
                                        <Button
                                            style={{width: 125, marginRight: 8}}
                                            type={'primary'}
                                            size={'small'}
                                            // loading={loading}
                                            onClick={async (e) => {
                                                const zipFile = await HecRasHelper.compressProject(tempProject)

                                                const element = document.createElement("a");
                                                element.setAttribute("href", window.URL.createObjectURL(zipFile));
                                                element.setAttribute("download", `${tempProject.information.title}_${tempProject.information.folderName}.zip`);
                                                element.style.display = "none";
                                                document.body.appendChild(element);
                                                element.click();
                                                document.body.removeChild(element);
                                            }}
                                        >
                                            Download
                                        </Button>
                                    </>
                                )
                            )
                        }}
                        items={[
                            {label: 'Project Files', key: 'Project Files'},
                            {label: 'Project Layers', key: 'additional'},
                        ]}
                        type={'card'}
                        activeKey={tab}
                        onChange={(tab) => setTab(tab)}
                    />
                    {
                        tab === 'Project Files' && (
                            <Table
                                size={'small'}
                                dataSource={tempProject.projectFiles}
                                pagination={false}
                                rowKey={'id'}
                                id={'id'}
                                scroll={{x: 1400}}
                                expandable={{
                                    showExpandColumn: false,
                                    expandedRowRender: renderRowExpand,
                                    rowExpandable: () => true,
                                    expandedRowKeys: expandedKeys,
                                    onExpand: (expanded, record) => {
                                        const keys = expandedKeys.filter(x => x !== record.id)
                                        if (keys.length !== expandedKeys.length)
                                            setExpandedKeys(keys)
                                        else
                                            setExpandedKeys([
                                                ...expandedKeys, record.id
                                            ])

                                        return true
                                    }
                                }}
                                columns={[
                                    {
                                        dataIndex: '#',
                                        width: 50,
                                        title: () => (
                                            <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
                                                <Button
                                                    size={'small'}
                                                    style={{width: 16, height: 16}}
                                                    icon={expandedKeys.length > 0 ?
                                                        <MinusOutlined style={{fontSize: '7pt'}}/> :
                                                        <PlusOutlined style={{fontSize: '7pt'}}/>}
                                                    onClick={() => {
                                                        if (expandedKeys.length > 0) {
                                                            setExpandedKeys([])
                                                        } else {
                                                            const keys = []
                                                            project.projectFiles?.forEach(x => {
                                                                if (x.mapProfiles.filter(x => x.target).length > 0)
                                                                    keys.push(x.id)
                                                            })

                                                            setExpandedKeys(keys)
                                                        }
                                                    }}
                                                />
                                            </div>
                                        ),
                                        key: '#',
                                        render: (text, row) => (
                                            <div style={{display: 'flex', alignItems: 'center', gap: 4, marginLeft: 6}}>
                                                <Button
                                                    size={'small'}
                                                    icon={expandedKeys.find(x => x === row.id) ?
                                                        <MinusOutlined style={{fontSize: '7pt'}}/> :
                                                        <PlusOutlined style={{fontSize: '7pt'}}/>}
                                                    style={{width: 16, height: 16}}
                                                    onClick={() => {
                                                        const keys = expandedKeys.filter(x => x !== row.id)
                                                        if (keys.length !== expandedKeys.length)
                                                            setExpandedKeys(keys)
                                                        else
                                                            setExpandedKeys([
                                                                ...expandedKeys, row.id
                                                            ])
                                                    }}
                                                />
                                                <Tooltip title={`Remove ${row?.plan?.title} Plan`}>
                                                    <Button
                                                        size={'small'}
                                                        type={'text'}
                                                        icon={<CloseOutlined style={{fontSize: '7pt'}}/>}
                                                        onClick={() => {
                                                            remove(row)
                                                        }}
                                                    />
                                                </Tooltip>
                                            </div>
                                        )
                                    },
                                    {
                                        width: 250,
                                        dataIndex: 'plan',
                                        title: 'Plan',
                                        key: 'plan',
                                        render: (text, row) => renderFile(row.plan)
                                    },
                                    {
                                        dataIndex: 'flow',
                                        width: 450,
                                        title: () => (
                                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                <div>
                                                    Flow
                                                </div>
                                                <Switch
                                                    checked={!(tempProject.useProfileLabel?.toString() === 'true')}
                                                    type={'primary'}
                                                    size={'small'}
                                                    onChange={(e) => {
                                                        const temp = {
                                                            ...tempProject
                                                        }

                                                        temp?.projectFiles?.forEach((plan, idx) => {
                                                            if (!e) {
                                                                plan?.mapProfiles?.forEach(x => {
                                                                    x._target = x.target;
                                                                    x._targetName = x.targetName;
                                                                    x.target = x.select;
                                                                    x.targetName = x.select;
                                                                })
                                                            } else {
                                                                plan?.mapProfiles?.forEach(x => {
                                                                    x.target = x._target;
                                                                    x.targetName = x._targetName;
                                                                })
                                                            }

                                                            plan.mappedProfiles = HecRasHelper.mapProfiles(plan.profileData, plan.mapProfiles)
                                                        })

                                                        setTempProject({
                                                            ...temp,
                                                            useProfileLabel: !e,
                                                            isChanged: true,
                                                        })
                                                    }}
                                                />
                                            </div>
                                        ),
                                        key: 'flow',
                                        render: (text, row) => renderFile(row.flow)
                                    },
                                    {
                                        dataIndex: 'geometry',
                                        width: 300,
                                        title: 'Geometry',
                                        key: 'geometry',
                                        render: (text, row) => renderFile(row.geometry)
                                    },
                                    {
                                        width: 250,
                                        dataIndex: 'output',
                                        title: () => {
                                            return (
                                                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                    Output
                                                    <Button
                                                        key={'run-output'}
                                                        // loading={isRunOutput}
                                                        disabled={isRunOutput}
                                                        icon={<CaretRightOutlined />}
                                                        size={'small'}
                                                        type={'primary'}
                                                        onClick={async () => {
                                                            if(isRunOutput)
                                                                return

                                                            setRunOutput(true)
                                                            for (const pf of tempProject?.projectFiles) {
                                                                const data = {
                                                                    project: {
                                                                        text: tempProject?.information?.text,
                                                                        file: tempProject?.information?.fileName,
                                                                    },
                                                                    plan: {
                                                                        file: pf?.plan?.fileName,
                                                                        text: pf?.plan?.text,
                                                                        name: pf?.plan?.shortIdentifier,
                                                                        profiles: pf?.mapProfiles,
                                                                        version: parseInt(pf?.plan?.version?.replaceAll('.', ''))
                                                                    },
                                                                    geometry: {
                                                                        file: pf?.geometry?.fileName,
                                                                        text: pf?.geometry?.text
                                                                    },
                                                                    flow: {
                                                                        file: pf?.flow?.fileName,
                                                                        text: pf?.flow?.text
                                                                    },
                                                                }

                                                                let timer = 0;
                                                                setRunOutputText(`${pf?.plan?.fileName} (${timer.toFixed(1)}.0 s)`)
                                                                const interval = setInterval(() => {
                                                                    timer += 0.2
                                                                    setRunOutputText(`${pf?.plan?.fileName} (${timer.toFixed(1)} s)`)
                                                                }, 200)

                                                                const res = await LocalService.run_project(data)

                                                                pf.profileData = res.result
                                                                pf.outputBinFile = {
                                                                    id: v4(),
                                                                    fileName: `${pf?.plan?.fileName?.split('.')[0]}.${pf?.plan?.outputFileExt}`,
                                                                    ext: pf?.plan?.outputFileExt,
                                                                    text: res?.outputBin
                                                                };
                                                                pf.mappedProfiles = HecRasHelper.mapProfiles(pf.profileData, pf.mapProfiles)
                                                                pf.isFindOutput = 'true'

                                                                clearInterval(interval)
                                                            }

                                                            tempProject.information.hasOutputFile = 'true'
                                                            setTempProject({
                                                                ...tempProject,
                                                                isChanged: true,
                                                                key: v4()
                                                            })

                                                            setRunOutput(false)
                                                            setRunOutputText('')
                                                        }}
                                                    />
                                                    {runOutputText}
                                                </div>
                                            )
                                        },
                                        key: 'output',
                                        render: (text, row) => renderOutputCol(row)
                                    },
                                    {
                                        dataIndex: 'type',
                                        title: () => (
                                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                <div>
                                                    Type
                                                </div>
                                                <Switch
                                                    checked={!(tempProject.useProjectLabel?.toString() === 'true')}
                                                    type={'primary'}
                                                    size={'small'}
                                                    onChange={(e) => {
                                                        const temp = {
                                                            ...tempProject
                                                        }

                                                        temp?.projectFiles?.forEach((row, idx) => {
                                                            if (!e) {
                                                                row.labelType = row.type.toString()
                                                                row.type = getPlanId(row).toString()
                                                            } else {
                                                                temp?.projectFiles?.forEach((row, idx) => {
                                                                    row.type = row.labelType.toString()
                                                                })
                                                            }
                                                        })

                                                        setTempProject({
                                                            ...temp,
                                                            isChanged: true,
                                                            useProjectLabel: !e,
                                                        })
                                                    }}
                                                />
                                            </div>
                                        ),
                                        key: 'type',
                                        render: (text, row) => {
                                            return (
                                                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                    <Select
                                                        size={'small'}
                                                        defaultValue={row.type}
                                                        style={{width: 150}}
                                                        onSelect={(val) => {
                                                            onTypeChanged(row, val)
                                                        }}
                                                        placeholder={'Select Type'}
                                                        allowClear={true}
                                                        onClear={() => {
                                                            onTypeChanged(row, '')
                                                        }}
                                                    >
                                                        <Select.Option value={getPlanId(row)}
                                                                       key={getPlanId(row)}>{getPlanId(row)}</Select.Option>
                                                        {
                                                            PlanTypes.map(x => (<Select.Option value={x.label}
                                                                                               key={x.label}>{x.label}</Select.Option>))
                                                        }
                                                    </Select>
                                                    {
                                                        !row?.hasGeoReferenced && (
                                                            <Popover
                                                                trigger={'click'}
                                                                placement={'bottomRight'}
                                                                content={(
                                                                    <div style={{width: 400, fontSize: '10pt'}}>
                                                                        Reach does not have (or incorrect) the georeferencing data (Schematic X, Y) in the geometry file. Therefore, the <b>{row?.plan?.fileName}</b> plan cannot be reviewed in Map View.
                                                                    </div>
                                                                )}
                                                            >
                                                                <Button
                                                                    size={'small'}
                                                                    type={'text'}
                                                                    icon={<WarningOutlined />}
                                                                />
                                                            </Popover>
                                                        )
                                                    }
                                                </div>
                                            )
                                        }
                                    },
                                ]}
                            />
                        )
                    }
                    {
                        tab === 'additional' && (
                            <div style={{display: tab === 'additional' ? 'block' : 'none'}}>
                                <div style={{marginBottom: 8, display: 'flex', justifyContent: 'flex-end'}}>
                                    <Button
                                        size={'small'}
                                        icon={<DownloadOutlined />}
                                        disabled={!tempProject?.shapeFiles || tempProject?.shapeFiles?.length === 0}
                                        onClick={async () => {
                                            try {
                                                const JSZip = (await import('jszip')).default;
                                                const zip = new JSZip();
                                                
                                                for (const shape of tempProject?.shapeFiles || []) {
                                                    let content;
                                                    if (typeof shape.data === 'string') {
                                                        content = shape.data;
                                                    } else if (shape.data instanceof Blob) {
                                                        content = await shape.data.arrayBuffer();
                                                    } else {
                                                        content = JSON.stringify(shape.data, null, 2);
                                                    }
                                                    zip.file(shape.fileName, content);
                                                }
                                                
                                                const zipBlob = await zip.generateAsync({ type: 'blob' });
                                                const url = window.URL.createObjectURL(zipBlob);
                                                const element = document.createElement('a');
                                                element.setAttribute('href', url);
                                                element.setAttribute('download', `${tempProject.information.title}_layers.zip`);
                                                element.style.display = 'none';
                                                document.body.appendChild(element);
                                                element.click();
                                                document.body.removeChild(element);
                                                window.URL.revokeObjectURL(url);
                                            } catch (error) {
                                                console.error('Download all layers error:', error);
                                            }
                                        }}
                                    >
                                        Download All Layers
                                    </Button>
                                </div>
                                <ShapeTable
                                    shapes={tempProject?.shapeFiles}
                                    onChange={(shapes) => {
                                        setTempProject({
                                            ...tempProject,
                                            shapeFiles: [...shapes],
                                            isChanged: true
                                        })
                                    }}
                                />
                            </div>
                        )
                    }
                </div>
            </div>
        </>
    )
}
